/**
 * What the app says about a failed publish, by the code the worker records
 * on the result (PostResult.errorCode). Every screen that shows a failure
 * (calendar, All Posts, quick edit, Attention, the post_failed notification,
 * the receipt) reads this map, so one failure reads the same everywhere: a
 * title, a one-line reason and one action. The channel's own message is
 * never the main text; it goes under "Details".
 *
 * Copy keys live under `publishError.<copy>` in the web app's locale files,
 * each taking `{{platform}}`. A unit test in the worker fails when the
 * publisher can write a code this map does not cover.
 */

/** The one thing the reader can do about a failure. */
export type PublishErrorAction =
  | 'reconnect'
  | 'edit_post'
  | 'retry'
  | 'replace_media'
  | 'buy_x_credits'
  | 'wait'
  | 'check_on_platform'
  | 'contact_support'
  /** The account lapsed: the fix is a plan, which no other action reaches. */
  | 'choose_plan';

/** The copy entries under `publishError.*`; several codes share one. */
export type PublishErrorCopyId =
  | 'mayBeLive'
  | 'postedNotRecorded'
  | 'reconnect'
  | 'permission'
  | 'xMediaScope'
  | 'xCredits'
  | 'mediaDeleted'
  | 'mediaRejected'
  | 'mediaRequired'
  | 'notInTime'
  | 'contentRules'
  | 'tooLong'
  | 'phoneNumber'
  | 'pinterestLink'
  | 'pinterestBoard'
  | 'gbpLocation'
  | 'rateLimited'
  | 'blocked'
  | 'tiktokSettings'
  | 'inReview'
  | 'duplicate'
  | 'setupProblem'
  | 'accountLapsed'
  | 'dailyLimit'
  | 'youtubeSettings'
  | 'telegramBot'
  | 'telegramRights'
  | 'channelPaused'
  | 'unknown';

export interface PublishErrorCopy {
  copy: PublishErrorCopyId;
  titleKey: string;
  reasonKey: string;
  action: PublishErrorAction;
  /**
   * The irreversible call got no answer, or went out unrecorded: the post
   * may already be up. Never offer Retry for these.
   */
  mayBeLive: boolean;
}

function entry(copy: PublishErrorCopyId, action: PublishErrorAction): PublishErrorCopy {
  return {
    copy,
    titleKey: `publishError.${copy}.title`,
    reasonKey: `publishError.${copy}.reason`,
    action,
    // inReview: the channel holds the post; sending it again would make a second copy.
    mayBeLive: copy === 'mayBeLive' || copy === 'postedNotRecorded' || copy === 'inReview',
  };
}

/**
 * Codes the worker itself writes, whatever the channel: its own error
 * classes (`readonly code`), the literal codes it passes to
 * PlatformApiError, and the codes of its X media and Google Business helpers.
 */
export const PUBLISH_ERROR_COPY: Readonly<Record<string, PublishErrorCopy>> = {
  // The post may be live: look before publishing again.
  PUBLISH_OUTCOME_UNKNOWN: entry('mayBeLive', 'check_on_platform'),
  POSTED_NOT_RECORDED: entry('postedNotRecorded', 'check_on_platform'),
  X_POSTED_NOT_RECORDED: entry('postedNotRecorded', 'check_on_platform'),

  // The connection.
  TOKEN_NOT_FOUND: entry('reconnect', 'reconnect'),
  TOKEN_INACTIVE: entry('reconnect', 'reconnect'),
  TOKEN_EXPIRED: entry('reconnect', 'reconnect'),
  MISSING_IG_USER_ID: entry('reconnect', 'reconnect'),
  MISSING_PAGE_ID: entry('reconnect', 'reconnect'),
  MISSING_AUTHOR_URN: entry('reconnect', 'reconnect'),
  MISSING_CHAT_ID: entry('reconnect', 'reconnect'),
  MISSING_LOCATION: entry('gbpLocation', 'reconnect'),
  X_RECONNECT_FOR_MEDIA: entry('xMediaScope', 'reconnect'),
  // Telegram: the bot was removed from the chat (kicked, not a member, chat
  // gone) — it is added back and the channel reconnected; or it is there
  // without the right to post.
  TELEGRAM_BOT_REMOVED: entry('telegramBot', 'reconnect'),
  TELEGRAM_BOT_RIGHTS: entry('telegramRights', 'retry'),
  // An account Meta has restricted or flagged (subcodes 2207050, 2207051).
  ACCOUNT_RESTRICTED: entry('blocked', 'check_on_platform'),

  // Plan and credits.
  X_POST_LIMIT_REACHED: entry('xCredits', 'buy_x_credits'),
  // A channel's own daily cap: Instagram's API-published posts in 24 hours,
  // YouTube's daily uploads of a channel. Waiting a day is the fix.
  IG_PUBLISH_LIMIT: entry('dailyLimit', 'wait'),
  DAILY_UPLOAD_LIMIT: entry('dailyLimit', 'wait'),
  // A channel answered 429 with no code of its own.
  RATE_LIMITED: entry('rateLimited', 'wait'),
  ACCOUNT_LAPSED: entry('accountLapsed', 'choose_plan'),
  // D-01: the plan connects fewer channels than the brand has; this one is paused.
  CHANNEL_PAUSED: entry('channelPaused', 'choose_plan'),

  // Media.
  MEDIA_DELETED: entry('mediaDeleted', 'replace_media'),
  PHOTO_CONVERSION_FAILED: entry('mediaRejected', 'replace_media'),
  UNSUPPORTED_MEDIA: entry('mediaRejected', 'replace_media'),
  CONTAINER_ERROR: entry('mediaRejected', 'replace_media'),
  VIDEO_PROCESSING_FAILED: entry('mediaRejected', 'replace_media'),
  MEDIA_PROCESSING_FAILED: entry('mediaRejected', 'replace_media'),
  REEL_REQUIRES_VIDEO: entry('mediaRequired', 'replace_media'),
  STORY_REQUIRES_MEDIA: entry('mediaRequired', 'replace_media'),
  VIDEO_REQUIRED: entry('mediaRequired', 'replace_media'),
  MEDIA_REQUIRED: entry('mediaRequired', 'replace_media'),
  // Facebook could not process a Reel or a video story it was handed.
  FB_VIDEO_PROCESSING_FAILED: entry('mediaRejected', 'replace_media'),

  // The channel took too long, or a step it answers did not come back: worth another go.
  CONTAINER_EXPIRED: entry('notInTime', 'retry'),
  CONTAINER_TIMEOUT: entry('notInTime', 'retry'),
  MEDIA_UPLOAD_FAILED: entry('notInTime', 'retry'),
  MISSING_UPLOAD_URL: entry('notInTime', 'retry'),
  VIDEO_PROCESSING: entry('notInTime', 'retry'),
  VIDEO_PROCESSING_TIMEOUT: entry('notInTime', 'retry'),
  MEDIA_INIT_FAILED: entry('notInTime', 'retry'),
  MEDIA_PROCESSING_TIMEOUT: entry('notInTime', 'retry'),
  NO_RESPONSE: entry('notInTime', 'retry'),
  // The publish never started (the queue could not be reached); the API's
  // publish recovery sweep records it so the post can be retried.
  PUBLISH_NOT_QUEUED: entry('notInTime', 'retry'),
  // A raw network error recorded as it was (axios and Node codes).
  ECONNABORTED: entry('notInTime', 'retry'),
  ETIMEDOUT: entry('notInTime', 'retry'),
  ECONNRESET: entry('notInTime', 'retry'),

  // The post itself.
  EMPTY_TWEET: entry('contentRules', 'edit_post'),
  EMPTY_THREAD: entry('contentRules', 'edit_post'),
  EMPTY_POST: entry('contentRules', 'edit_post'),
  INVALID_CAROUSEL_COUNT: entry('contentRules', 'edit_post'),
  // X refused the text as a copy of a recent post.
  DUPLICATE_CONTENT: entry('duplicate', 'edit_post'),
  MISSING_SCHEDULE: entry('contentRules', 'edit_post'),
  TOO_LONG: entry('tooLong', 'edit_post'),
  PHONE_NUMBER: entry('phoneNumber', 'edit_post'),
  MISSING_DESTINATION_URL: entry('pinterestLink', 'edit_post'),
  MISSING_BOARD_ID: entry('pinterestBoard', 'edit_post'),
  // The uploader's YouTube choices are incomplete (title, privacy, kids, altered content).
  YOUTUBE_CHOICES: entry('youtubeSettings', 'edit_post'),
  // A LinkedIn poll without its question and answers, an article without its link and title.
  LINKEDIN_POLL_INCOMPLETE: entry('contentRules', 'edit_post'),
  LINKEDIN_ARTICLE_INCOMPLETE: entry('contentRules', 'edit_post'),
  // TikTok's Direct Post checks before publishing (platform-constants tiktok.ts).
  TIKTOK_PRIVACY_REQUIRED: entry('tiktokSettings', 'edit_post'),
  TIKTOK_PRIVACY_INVALID: entry('tiktokSettings', 'edit_post'),
  TIKTOK_PRIVACY_NOT_OFFERED: entry('tiktokSettings', 'edit_post'),
  TIKTOK_BRANDED_PRIVATE: entry('tiktokSettings', 'edit_post'),
  TIKTOK_DISCLOSURE_INCOMPLETE: entry('tiktokSettings', 'edit_post'),
  TIKTOK_COMMENTS_DISABLED: entry('tiktokSettings', 'edit_post'),
  TIKTOK_DUET_DISABLED: entry('tiktokSettings', 'edit_post'),
  TIKTOK_STITCH_DISABLED: entry('tiktokSettings', 'edit_post'),
  TIKTOK_VIDEO_TOO_LONG: entry('mediaRejected', 'replace_media'),
  // Zetpin's moderation: held for review (accepted, not live), or refused.
  ZETPIN_PENDING_REVIEW: entry('inReview', 'check_on_platform'),
  ZETPIN_REJECTED: entry('contentRules', 'edit_post'),
  ZETPIN_LOCATION_REQUIRED: entry('contentRules', 'edit_post'),
  MISSING_ACCOUNT_ID: entry('reconnect', 'reconnect'),

  // Our side: not something the reader did.
  TOKEN_REFRESH_NOT_CONFIGURED: entry('setupProblem', 'contact_support'),
  MISSING_CREDENTIALS: entry('setupProblem', 'contact_support'),
};

/**
 * Codes a channel's API returns that the worker records as they are
 * (Meta's numeric codes, X's problem types, TikTok's error codes, Google's
 * statuses). Keyed by platform, since the same code means different things
 * on different APIs.
 */
const META_CODES: Readonly<Record<string, PublishErrorCopy>> = {
  // OAuthException: the token was invalidated or expired.
  '190': entry('reconnect', 'reconnect'),
  // Permission missing or revoked.
  '10': entry('permission', 'reconnect'),
  '200': entry('permission', 'reconnect'),
  // Rate limits (app, user, page, custom).
  '4': entry('rateLimited', 'wait'),
  '17': entry('rateLimited', 'wait'),
  '32': entry('rateLimited', 'wait'),
  '613': entry('rateLimited', 'wait'),
  // Temporarily blocked from posting.
  '368': entry('blocked', 'check_on_platform'),
  // Duplicate post.
  '506': entry('duplicate', 'edit_post'),
  // Media could not be fetched, or does not fit (aspect ratio, format).
  '9004': entry('mediaRejected', 'replace_media'),
  '36003': entry('mediaRejected', 'replace_media'),
  // Media not ready yet.
  '9007': entry('notInTime', 'retry'),
  // Instagram's daily publishing limit (code 9, subcode 2207042 is IG_PUBLISH_LIMIT).
  '9': entry('dailyLimit', 'wait'),
  // Container gone or expired before it was published: made again on a retry.
  '24': entry('notInTime', 'retry'),
  // Media Instagram cannot take: too large (36000), wrong format (352).
  '36000': entry('mediaRejected', 'replace_media'),
  '352': entry('mediaRejected', 'replace_media'),
  // A Facebook Reel's aspect ratio, resolution, length or frame rate.
  '1363040': entry('mediaRejected', 'replace_media'),
  '1363127': entry('mediaRejected', 'replace_media'),
  '1363128': entry('mediaRejected', 'replace_media'),
  '1363129': entry('mediaRejected', 'replace_media'),
};

export const PLATFORM_PUBLISH_ERROR_COPY: Readonly<Record<string, Readonly<Record<string, PublishErrorCopy>>>> = {
  INSTAGRAM: META_CODES,
  FACEBOOK: META_CODES,
  TWITTER: {
    'usage-capped': entry('rateLimited', 'wait'),
    'not-authorized-for-resource': entry('permission', 'reconnect'),
    'client-forbidden': entry('setupProblem', 'contact_support'),
    'invalid-request': entry('contentRules', 'edit_post'),
  },
  TIKTOK: {
    rate_limit_exceeded: entry('rateLimited', 'wait'),
    spam_risk_too_many_posts: entry('rateLimited', 'wait'),
    reached_active_user_cap: entry('rateLimited', 'wait'),
    access_token_invalid: entry('reconnect', 'reconnect'),
    scope_not_authorized: entry('permission', 'reconnect'),
    spam_risk_user_banned_from_posting: entry('blocked', 'check_on_platform'),
    privacy_level_option_mismatch: entry('tiktokSettings', 'edit_post'),
    unaudited_client_can_only_post_to_private_accounts: entry('tiktokSettings', 'edit_post'),
    url_ownership_unverified: entry('setupProblem', 'contact_support'),
    // fail_reason of a publish TikTok took and then failed.
    file_format_check_failed: entry('mediaRejected', 'replace_media'),
    duration_check_failed: entry('mediaRejected', 'replace_media'),
    frame_rate_check_failed: entry('mediaRejected', 'replace_media'),
    picture_size_check_failed: entry('mediaRejected', 'replace_media'),
    video_pull_failed: entry('notInTime', 'retry'),
    photo_pull_failed: entry('notInTime', 'retry'),
    internal: entry('notInTime', 'retry'),
  },
  // Zetpin's Developer API errors ({ error: '<code>' }).
  ZETPIN: {
    quota_exceeded: entry('rateLimited', 'wait'),
    rate_limited: entry('rateLimited', 'wait'),
    invalid_token: entry('reconnect', 'reconnect'),
    insufficient_scope: entry('permission', 'reconnect'),
  },
  GOOGLE_BUSINESS: {
    PERMISSION_DENIED: entry('permission', 'reconnect'),
    UNAUTHENTICATED: entry('reconnect', 'reconnect'),
    RESOURCE_EXHAUSTED: entry('rateLimited', 'wait'),
  },
  // LinkedIn: the worker's platform-errors.ts classifier codes (review PR-02),
  // then LinkedIn's own `code` (Posts API) or, without one, its serviceErrorCode.
  LINKEDIN: {
    TOKEN_REVOKED: entry('reconnect', 'reconnect'),
    PERMISSION_DENIED: entry('permission', 'reconnect'),
    RATE_LIMITED: entry('rateLimited', 'wait'),
    ACCESS_DENIED: entry('permission', 'reconnect'),
    // serviceErrorCode 100: not enough permissions; 65600-65602: the token is invalid, revoked or expired.
    '100': entry('permission', 'reconnect'),
    '65600': entry('reconnect', 'reconnect'),
    '65601': entry('reconnect', 'reconnect'),
    '65602': entry('reconnect', 'reconnect'),
    TOO_MANY_REQUESTS: entry('rateLimited', 'wait'),
    MISSING_FIELD: entry('contentRules', 'edit_post'),
    INVALID_VALUE_BLANK_FIELD: entry('contentRules', 'edit_post'),
    FIELD_LENGTH_TOO_LONG: entry('tooLong', 'edit_post'),
    UNPROCESSABLE_ENTITY: entry('contentRules', 'edit_post'),
    DUPLICATE_POST: entry('duplicate', 'edit_post'),
  },
  // YouTube: the classifier codes, then Google's error.errors[0].reason, as it is.
  YOUTUBE: {
    TOKEN_REVOKED: entry('reconnect', 'reconnect'),
    PERMISSION_DENIED: entry('permission', 'reconnect'),
    RATE_LIMITED: entry('rateLimited', 'wait'),
    quotaExceeded: entry('rateLimited', 'wait'),
    rateLimitExceeded: entry('rateLimited', 'wait'),
    userRateLimitExceeded: entry('rateLimited', 'wait'),
    dailyLimitExceeded: entry('rateLimited', 'wait'),
    uploadLimitExceeded: entry('dailyLimit', 'wait'),
    youtubeSignupRequired: entry('reconnect', 'reconnect'),
    authError: entry('reconnect', 'reconnect'),
    expired: entry('reconnect', 'reconnect'),
    forbidden: entry('permission', 'reconnect'),
    insufficientPermissions: entry('permission', 'reconnect'),
    accountDelegationForbidden: entry('permission', 'reconnect'),
    accountDisabled: entry('blocked', 'check_on_platform'),
    channelClosed: entry('blocked', 'check_on_platform'),
    channelSuspended: entry('blocked', 'check_on_platform'),
    invalidTitle: entry('youtubeSettings', 'edit_post'),
    invalidDescription: entry('contentRules', 'edit_post'),
    invalidCategoryId: entry('youtubeSettings', 'edit_post'),
    forbiddenPrivacySetting: entry('youtubeSettings', 'edit_post'),
    mediaBodyRequired: entry('mediaRequired', 'replace_media'),
  },
  // Pinterest's numeric `code` has no public list: a 401 or 429 is worded by
  // the worker's own codes (TOKEN_EXPIRED, RATE_LIMITED) and the classifier's.
  PINTEREST: {
    TOKEN_REVOKED: entry('reconnect', 'reconnect'),
    PERMISSION_DENIED: entry('permission', 'reconnect'),
    RATE_LIMITED: entry('rateLimited', 'wait'),
    // The app is still on Pinterest Trial access: our setup, not the customer's.
    PINTEREST_TRIAL_ACCESS: entry('setupProblem', 'contact_support'),
  },
};

/** Any code the app has no words for: the channel's message goes under "Details". */
export const PUBLISH_ERROR_FALLBACK: PublishErrorCopy = entry('unknown', 'retry');

/** What to say about a failed result: its code's copy, else the fallback. */
export function publishErrorCopy(platform: string, code: string | null | undefined): PublishErrorCopy {
  if (!code) return PUBLISH_ERROR_FALLBACK;
  return PUBLISH_ERROR_COPY[code] ?? PLATFORM_PUBLISH_ERROR_COPY[platform.toUpperCase()]?.[code] ?? PUBLISH_ERROR_FALLBACK;
}

/** Whether the app has words for this code (the fallback does not count). */
export function hasPublishErrorCopy(platform: string, code: string | null | undefined): boolean {
  return publishErrorCopy(platform, code) !== PUBLISH_ERROR_FALLBACK;
}
